import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Stack,
  Tooltip,
  alpha
} from "@mui/material";
import { Close, SettingsInputComponentRounded, StorageRounded, DescriptionRounded, BadgeRounded, InfoOutlined } from "@mui/icons-material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";

// Scopes Update/Create Component
function ScopesUpdate({
  handleModalClose,
  scopeData,
  refetch,
  isNew
}) {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: "onChange",
    defaultValues: isNew ? { name: "", description: "", resource_server_name: "" } : scopeData
  });

  // DATA FETCHING
  const { data: serversData } = useQuery(["resource_servers_list"], () =>
    axiosPrivate.get(`/resourceServers`, { params: { page: -1 } }),
    { refetchOnWindowFocus: false }
  );

  // MUTATIONS
  const { mutate: create, isLoading: isCreating } = useMutation((formData) =>
    axiosPrivate.post(`/oauth/scopes`, formData)
  );

  const { mutate: update, isLoading: isUpdating } = useMutation((formData) =>
    axiosPrivate.put(`/oauth/scopes/${scopeData.name}`, formData)
  );

  const onSubmit = (formData) => {
    const action = isNew ? create : update;
    action(formData, {
      onSuccess: () => {
        enqueueSnackbar(`Scope ${isNew ? 'registered' : 'updated'} successfully.`, { variant: "success" });
        handleModalClose();
        refetch();
      },
      onError: (err) => {
        const message = err?.response?.data?.error?.message || "Operation failed";
        enqueueSnackbar(message, { variant: "error" });
      }
    });
  };

  const actionLoading = isCreating || isUpdating;

  return (
    <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '480px' },
          borderRadius: { xs: '24px 24px 0 0', sm: 6 },
          boxShadow: theme => theme.palette.mode === 'light'
            ? "0px 20px 60px rgba(0, 0, 0, 0.15)"
            : `0px 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
          overflow: 'hidden',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', borderRadius: 2 }}>
              <SettingsInputComponentRounded />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{isNew ? "Register Scope" : "Update Scope"}</Typography>
          </Stack>
          <IconButton onClick={handleModalClose} size="small"><Close /></IconButton>
        </Box>

        <Box sx={{ p: 4, overflowY: 'auto' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <FormSection title="Scope Identity" icon={<BadgeRounded fontSize="inherit" />} hint="Scope names are unique identifiers and cannot contain spaces.">
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: "Scope name is required",
                      pattern: { value: /^\S+$/, message: "No whitespace allowed" }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Scope Identifier"
                        placeholder="e.g. read:profile"
                        disabled={!isNew}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                      />
                    )}
                  />
                </Box>
              </FormSection>

              <FormSection title="Assignment" icon={<StorageRounded fontSize="inherit" />}>
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="resource_server_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Assign to Resource Server"
                        disabled={!isNew}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                      >
                        <MenuItem value=""><em>Assign to Tenant (None)</em></MenuItem>
                        {serversData?.data?.data?.map((server) => (
                          <MenuItem key={server.id} value={server.name}>{server.name}</MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {!isNew && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                      Assignment cannot be changed after registration.
                    </Typography>
                  )}
                </Box>
              </FormSection>

              <FormSection title="Purpose" icon={<DescriptionRounded fontSize="inherit" />}>
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: "Description is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={3}
                        label="What does this permit?"
                        placeholder="Allows the application to read basic profile information."
                        error={!!errors.description}
                        helperText={errors.description?.message}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                      />
                    )}
                  />
                </Box>
              </FormSection>

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button fullWidth variant="outlined" color="inherit" onClick={handleModalClose} sx={{ borderRadius: 3, py: 1.2, fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={actionLoading}
                  sx={{
                    borderRadius: 3,
                    py: 1.2,
                    fontWeight: 700,
                    textTransform: 'none',
                    bgcolor: 'secondary.main',
                    '&:hover': { bgcolor: 'secondary.dark' },
                    boxShadow: theme => `0 8px 20px ${alpha(theme.palette.common.black, 0.2)}`
                  }}
                >
                  {actionLoading ? <CircularProgress size={24} color="inherit" /> : (isNew ? "Register" : "Update")}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Paper>
    </Grid>
  );
}

function FormSection({ title, icon, hint, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: 'primary.main', fontSize: 18, display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography>
        {hint && (
          <Tooltip title={hint} arrow>
            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Stack>
      {children}
    </Box>
  );
}

export default ScopesUpdate;